import { zodResolver } from "@hookform/resolvers/zod";
import { useAppDispatch, useAppSelector } from "hooks/hooks";
import { FC, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { getDemoHomeVideoById, updateDemoHomeVideos } from "redux/features/Admin/homeVideo/homeVideoSlice";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import DashboardMenu from "../../DashboardPage/DashboardMenu";
import { FiVideo, FiUpload, FiAlertCircle, FiCheckCircle } from "react-icons/fi";
import { z } from "zod";

export interface UpdateDemoVideoHomePageProps {
  className?: string;
}

const MAX_FILE_SIZE = 1024 * 1024 * 2000; // 2000MB
const ACCEPTED_VIDEO_MIME_TYPES = ["video/mp4", "video/mpeg", "video/webm", "video/mov"];
const formSchema = z.object({
  title: z.string().optional(),
  url: z
    .instanceof(File, { message: "La vidéo est requise." })
    .refine((file) => file?.size <= MAX_FILE_SIZE, `La taille maximale de la vidéo est de 2000MB.`)
    .refine(
      (file) => ACCEPTED_VIDEO_MIME_TYPES.includes(file?.type),
      "Le fichier doit être une vidéo au format MP4, MPEG, WEBM ou MOV."
    )
    .optional(),
});

const UpdateDemoVideoHomePage: FC<UpdateDemoVideoHomePageProps> = ({ className = "" }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    trigger,
    reset,
  } = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { title: "", url: undefined },
    mode: "onSubmit",
  });

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const videoState = useAppSelector((state) => state.videoHome);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [videoPreview, setVideoPreview] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      dispatch(getDemoHomeVideoById(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (videoState?.videoData) {
      setValue("title", videoState.videoData.title || "");
      if (videoState.videoData.url) {
        setVideoPreview(process.env.REACT_APP_CLOUD_FRONT + videoState.videoData.url);
      }
    }
  }, [videoState, setValue]);

  const watchedFile = watch("url");
  const watchedTitle = watch("title");
  useEffect(() => {
    if (watchedFile instanceof File) {
      const url = URL.createObjectURL(watchedFile);
      setVideoPreview(url);
      return () => URL.revokeObjectURL(url);
    } else if (videoState?.videoData?.url && !watchedFile) {
      setVideoPreview(process.env.REACT_APP_CLOUD_FRONT + videoState.videoData.url);
    }
  }, [watchedFile, videoState]);

  const onSubmit: SubmitHandler<z.infer<typeof formSchema>> = async (data) => {
    try {
      await dispatch(updateDemoHomeVideos({ data, id })).then((result) => {
        if (updateDemoHomeVideos.fulfilled.match(result)) {
          setSuccessMessage("Vidéo mise à jour avec succès !");

          reset({
            title: result.payload.title || data.title || "",
            url: undefined,
          });
          if (result.payload?.url) {
            setVideoPreview(process.env.REACT_APP_CLOUD_FRONT + result.payload.url);
          }
          setTimeout(() => {
            setSuccessMessage(null);
            navigate("/admin/dashboard/settings/home-demo-video");
          }, 2000);
        }
      });
    } catch (error) {
      console.error("Échec de la mise à jour :", error);
    }
  };

  useEffect(() => {
    switch (videoState.loading) {
      case "pending":
        setIsLoading(true);
        break;
      case "succeeded":
      case "failed":
        setIsLoading(false);
        break;
    }
  }, [videoState.loading]);

  return (
    <div
      className={`nc-AccountPage ${className} bg-gradient-to-br from-gray-50 to-gray-100 dark:from-neutral-900 dark:to-neutral-800 min-h-screen`}
      data-nc-id="AccountPage"
    >
      <Helmet>
        <title>EventToBee | Location d'espaces : salles, lofts, jardins & boutiques – Paris & Île-de-France</title>
      </Helmet>
      <DashboardMenu>
        <div className="container px-4 py-12 mx-auto">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-2xl p-8 mx-auto bg-white shadow-lg dark:bg-neutral-800 rounded-2xl"
          >
            <div className="flex items-center mb-8 space-x-3">
              <FiVideo className="text-3xl text-primary-600" />
              <h2 className="text-2xl font-extrabold text-gray-900 dark:text-white">Mettre à jour la vidéo</h2>
            </div>

            {successMessage && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex items-center p-4 mb-6 text-green-800 bg-green-100 rounded-lg dark:bg-green-900 dark:text-green-200"
              >
                <FiCheckCircle className="mr-2 text-xl" />
                {successMessage}
              </motion.div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Title Input */}
              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
                  Titre de la vidéo
                </label>
                <input
                  {...register("title")}
                  type="text"
                  className="w-full px-4 py-2 text-gray-900 transition-colors bg-white border border-gray-300 rounded-lg dark:border-neutral-600 dark:bg-neutral-700 dark:text-gray-100 focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                  placeholder="Enter video title"
                  value={watchedTitle || ""}
                />
                {errors.title?.message && (
                  <p className="flex items-center mt-1 text-xs text-red-500">
                    <FiAlertCircle className="mr-1" /> {errors.title.message}
                  </p>
                )}
              </div>

              {/* Video File Input */}
              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
                  Fichier vidéo
                </label>
                <div className="relative">
                  <input
                    type="file"
                    id="videoFile"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setValue("url", file);
                        trigger("url");
                      }
                    }}
                    accept="video/mp4,video/mpeg,video/webm,video/mov"
                  />
                  <label
                    htmlFor="videoFile"
                    className="flex items-center justify-center w-full px-4 py-3 text-gray-600 transition-colors border border-gray-300 border-dashed rounded-lg cursor-pointer dark:border-neutral-600 bg-gray-50 dark:bg-neutral-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-neutral-600"
                  >
                    <FiUpload className="mr-2 text-xl" />
                    {watchedFile ? watchedFile.name : "Choose a video file"}
                  </label>
                </div>
                {errors.url?.message && (
                  <p className="flex items-center mt-1 text-xs text-red-500">
                    <FiAlertCircle className="mr-1" /> {errors.url.message}
                  </p>
                )}
              </div>

              {/* Video Preview */}
              {videoPreview && (
                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
                    Video Preview
                  </label>
                  <video
                    className="w-full rounded-lg"
                    src={videoPreview}
                    controls
                    onError={() => setVideoPreview(null)}
                  />
                </div>
              )}

              {/* Submit Button */}
              <div className="flex justify-center pt-4">
                <ButtonPrimary
                  type="submit"
                  loading={isLoading}
                  className="w-full px-8 transition-transform duration-300 transform sm:w-auto hover:scale-105 btn-primary-gradient"
                >
                  Mettre à jour la vidéo
                </ButtonPrimary>
              </div>
            </form>
          </motion.div>
        </div>
      </DashboardMenu>
    </div>
  );
};

export default UpdateDemoVideoHomePage;
